import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useEffect } from "react";
import { NumericFormat } from "react-number-format";
import Header from "../../../../components/Header";
import {
  graphqlQuery,
  generateAndExecuteMutation,
} from "../../../../data/Axios/DynamicService";
import { AccountsandFinanceurl } from "../../../../config";
import {
  fixedAssetsCategories,
  allAssetTypes,
  allDepreciationMethods,
} from "../../../../data/Columns/Queries/FixedAssetManagement/fixedAssetsmanagement";

const base_url = AccountsandFinanceurl.uri;

const DepreciationScheduleForm = ({ isEditing = false, data = {}, onClose, refetchData }) => {
  const [initialValues, setInitialValues] = useState({
    categoryId: "",
    typeId: "",
    methodId: "",
    usefulLifeYears: "",
    salvageValue: "",
    totalEstimatedUnits: "",
  });

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id) || null;

  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [methods, setMethods] = useState([]);

  const isNonMobile = useMediaQuery("(min-width:600px)");

  // Fetch master data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const catData = await graphqlQuery(
          fixedAssetsCategories,
          base_url
        );
        setCategories(catData || []);

        // Fetch asset types
        const typeData = await graphqlQuery(
          allAssetTypes,
          base_url
        );
        setTypes(typeData || []);

        // Fetch depreciation methods
        const methodData = await graphqlQuery(
          allDepreciationMethods,
          base_url
        );
        setMethods(methodData || []);
        
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Populate data when editing
  useEffect(() => {
    if (isEditing && data) {
      setInitialValues({
        categoryId: data.categoryId?.toString() || "",
        typeId: data.typeId?.toString() || "",
        methodId: data.methodId?.toString() || "",
        usefulLifeYears: data.usefulLifeYears?.toString() || "",
        salvageValue: data.salvageValue?.toString() || "",
        totalEstimatedUnits: data.totalEstimatedUnits?.toString() || "",
      });
    }
  }, [isEditing, data]);

  const validationSchema = yup.object().shape({
    categoryId: yup.string().required("Category is required"),
    typeId: yup.string().required("Asset type is required"),
    methodId: yup.string().required("Depreciation method is required"),
    // usefulLifeYears: yup
    //   .number()
    //   .typeError("Must be a number")
    //   .positive("Must be positive")
    //   .integer("Must be an integer")
    //   .required("Useful life (years) is required"),
    // salvageValue: yup
    //   .number()
    //   .typeError("Must be a number")
    //   .min(0)
    //   .required("Salvage value is required"),
    // totalEstimatedUnits: yup
    //   .number()
    //   .typeError("Must be a number")
    //   .min(0)
    //   .required("Total estimated units is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const mutationData = {
        ...values,
        
        categoryId: parseInt(values.categoryId),
        typeId: parseInt(values.typeId),
        methodId: parseInt(values.methodId),
        usefulLifeYears: parseInt(values.usefulLifeYears),
        salvageValue: parseFloat(values.salvageValue),
        totalEstimatedUnits: parseFloat(values.totalEstimatedUnits),
        isActive: true,
        ...(isEditing
          ? { id: data.id, updatedBy: userId }
          : { createdBy: userId }),
      };

      const responseKey = isEditing ? "updatedSchedule" : "newSchedule";

      const response = await generateAndExecuteMutation(
        isEditing ? "updateDepreciationSchedule" : "createDepreciationSchedule",
        mutationData,
        responseKey,
        base_url
      );

      if (response) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Depreciation schedule ${isEditing ? "updated" : "created"} successfully.`,
        }).then(() => {
          if (refetchData) refetchData();
          if (onClose) onClose();
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Something went wrong while saving the schedule.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <Header
        title={isEditing ? "Edit Depreciation Schedule" : "Create Depreciation Schedule"}
        subtitle={isEditing ? "Update schedule details" : "Add a new depreciation schedule"}
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="25px"
              gridTemplateColumns="repeat(2, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              {/* Category */}
              <FormControl fullWidth variant="filled">
                <InputLabel>Category *</InputLabel>
                <Select
                  name="categoryId"
                  value={values.categoryId}
                  onChange={handleChange}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.categoryName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Type */}
              <FormControl fullWidth variant="filled">
                <InputLabel>Asset Type *</InputLabel>
                <Select
                  name="typeId"
                  value={values.typeId}
                  onChange={handleChange}
                >
                  {types.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.typeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Method */}
              <FormControl fullWidth variant="filled">
                <InputLabel>Depreciation Method *</InputLabel>
                <Select
                  name="methodId"
                  value={values.methodId}
                  onChange={handleChange}
                >
                  {methods.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.methodName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                variant="filled"
                label="Useful Life (Years) *"
                name="usefulLifeYears"
                type="number"
                value={values.usefulLifeYears}
                onChange={handleChange}
                error={touched.usefulLifeYears && !!errors.usefulLifeYears}
                helperText={touched.usefulLifeYears && errors.usefulLifeYears}
              />

              <NumericFormat
                customInput={TextField}
                fullWidth
                variant="filled"
                label="Salvage Value *"
                name="salvageValue"
                value={values.salvageValue}
                thousandSeparator=","
                allowNegative={false}
                onValueChange={(valuesObj) => {
                  setFieldValue("salvageValue", valuesObj.value);
                }}
                onBlur={handleBlur}
                error={touched.salvageValue && !!errors.salvageValue}
                helperText={touched.salvageValue && errors.salvageValue}
              />

              <NumericFormat
                customInput={TextField}
                fullWidth
                variant="filled"
                label="Total Estimated Units *"
                name="totalEstimatedUnits"
                value={values.totalEstimatedUnits}
                thousandSeparator=","
                allowNegative={false}
                onValueChange={(valuesObj) => {
                  setFieldValue("totalEstimatedUnits", valuesObj.value);
                }}
                onBlur={handleBlur}
                error={touched.totalEstimatedUnits && !!errors.totalEstimatedUnits}
                helperText={touched.totalEstimatedUnits && errors.totalEstimatedUnits}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : isEditing ? "Update" : "Create"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default DepreciationScheduleForm;